import { Link } from 'react-router-dom'
import type { Program, VideoVisibility, DifficultyLevel } from '../../types/database.types'
import type { AssignmentSummary } from '../../services/assignmentService'

interface ProgramCardProps {
  program: Program & { item_count?: number; total_duration?: number }
  assignmentSummary?: AssignmentSummary
  onDelete?: () => void
  onDuplicate?: () => void
  onToggleTemplate?: () => void
  onUseTemplate?: () => void
  onToggleVisibility?: () => void
  isTogglingVisibility?: boolean
}

const visibilityIcons: Record<VideoVisibility, React.ReactNode> = {
  public: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  organization: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  private: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  ),
}

const difficultyColors: Record<DifficultyLevel, string> = {
  beginner: 'bg-green-100 text-green-800',
  intermediate: 'bg-yellow-100 text-yellow-800',
  advanced: 'bg-orange-100 text-orange-800',
  expert: 'bg-red-100 text-red-800',
}

const visibilityLabels: Record<VideoVisibility, string> = {
  public: 'Public',
  organization: 'Organization',
  private: 'Private',
}

export function ProgramCard({
  program,
  assignmentSummary,
  onDelete,
  onDuplicate,
  onToggleTemplate,
  onUseTemplate,
  onToggleVisibility,
  isTogglingVisibility,
}: ProgramCardProps) {
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    if (mins < 60) return `${mins} min`
    const hrs = Math.floor(mins / 60)
    const remainingMins = mins % 60
    return `${hrs}h ${remainingMins}m`
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <Link
            to={`/dashboard/programs/${program.id}`}
            className="font-semibold text-gray-900 hover:text-blue-600 line-clamp-1"
          >
            {program.title}
          </Link>
          {program.description && (
            <p className="mt-1 text-sm text-gray-500 line-clamp-2">
              {program.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 ml-4">
          {onToggleVisibility ? (
            <button
              onClick={onToggleVisibility}
              disabled={isTogglingVisibility}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                program.visibility === 'public'
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } ${isTogglingVisibility ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={`Click to make ${program.visibility === 'public' ? 'private' : 'public'}`}
            >
              {isTogglingVisibility ? (
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                visibilityIcons[program.visibility]
              )}
              {visibilityLabels[program.visibility]}
            </button>
          ) : (
            <span
              className="text-gray-400"
              title={`${program.visibility} visibility`}
            >
              {visibilityIcons[program.visibility]}
            </span>
          )}
          {program.is_template && (
            <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
              Template
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {program.difficulty_level && (
          <span
            className={`px-2 py-0.5 text-xs rounded-full ${difficultyColors[program.difficulty_level]}`}
          >
            {program.difficulty_level}
          </span>
        )}
        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
          {program.duration_weeks ? `${program.duration_weeks} weeks` : 'Unlimited'}
        </span>
      </div>

      <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
        {program.item_count !== undefined && (
          <span className="flex items-center gap-1">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            {program.item_count} exercises
          </span>
        )}
        {program.total_duration !== undefined && program.total_duration > 0 && (
          <span className="flex items-center gap-1">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {formatDuration(program.total_duration)}
          </span>
        )}
      </div>

      {/* Assignment Summary Section */}
      {assignmentSummary && assignmentSummary.total_count > 0 ? (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Assignments
            </span>
            <div className="flex items-center gap-2">
              {assignmentSummary.active_count > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                  {assignmentSummary.active_count} Active
                </span>
              )}
              {assignmentSummary.completed_count > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                  {assignmentSummary.completed_count} Completed
                </span>
              )}
              {assignmentSummary.expired_count > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                  {assignmentSummary.expired_count} Expired
                </span>
              )}
            </div>
          </div>
          {/* Progress Bar */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Avg Progress:</span>
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  assignmentSummary.average_progress >= 80
                    ? 'bg-green-500'
                    : assignmentSummary.average_progress >= 50
                    ? 'bg-blue-500'
                    : 'bg-yellow-500'
                }`}
                style={{ width: `${Math.min(assignmentSummary.average_progress, 100)}%` }}
              />
            </div>
            <span className="text-xs font-medium text-gray-700 w-9 text-right">
              {Math.round(assignmentSummary.average_progress)}%
            </span>
          </div>
        </div>
      ) : assignmentSummary ? (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">No clients assigned yet</p>
        </div>
      ) : null}

      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-gray-400">
          Updated {new Date(program.updated_at).toLocaleDateString()}
        </span>
        <div className="flex gap-2 flex-wrap justify-end">
          {onUseTemplate && (
            <button
              onClick={onUseTemplate}
              className="text-sm text-green-600 hover:text-green-800 font-medium"
            >
              Use Template
            </button>
          )}
          {onToggleTemplate && (
            <button
              onClick={onToggleTemplate}
              className="text-sm text-purple-600 hover:text-purple-800"
            >
              {program.is_template ? 'Remove Template' : 'Save as Template'}
            </button>
          )}
          {onDuplicate && (
            <button
              onClick={onDuplicate}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Duplicate
            </button>
          )}
          <Link
            to={`/dashboard/programs/${program.id}/edit`}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Edit
          </Link>
          {onDelete && (
            <button
              onClick={onDelete}
              className="text-sm text-red-500 hover:text-red-700"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
